// What this side hears and sees of everyone else, and what it sends, as one
// small message to the room every ten seconds (worker/src/mediaHealth.ts
// keeps the latest per person and logs when someone stops hearing or seeing
// someone else). Adam, 2026-10-16, after a call where he stopped hearing his
// guest until the guest left and came back (QUEUE row 413): "why don't we
// have a sidecar/tail log for sound reaching end to end and we can just
// see?", then "same for video".
//
// Pure: CallSession.mediaCounters supplies the running totals, and the
// caller says which track each tile shows and which tracks are playing.

import type { InboundCounter, MediaCounters } from "./peer";

export interface PeerView {
  readonly id: string;
  /** The tracks the tile for this person holds (what the page plays). */
  readonly audioTrackId: string | null;
  readonly videoTrackId: string | null;
  readonly screenTrackId: string | null;
}

export interface HealthInputs {
  readonly prev: MediaCounters | null;
  readonly cur: MediaCounters;
  readonly peers: readonly PeerView[];
  /** Track ids an unpaused <audio> element is playing right now. */
  readonly playing: ReadonlySet<string>;
  readonly micOn: boolean;
  readonly cameraOn: boolean;
  readonly sharing: boolean;
  readonly hidden: boolean;
  /** This page's microphone: sent only digital silence for eight seconds
   * while on, the track's own state, and the connection type (row 506). */
  readonly micDead?: boolean;
  readonly micTrack?: { readonly en: boolean; readonly mu: boolean; readonly rs: string } | null;
  readonly net?: string | null;
}

/** Mean level (0 to 1) over the window, from a running sum of level squared
 * times seconds; null when no audio time passed. */
export function meanLevel(energyDelta: number, secsDelta: number): number | null {
  if (!(secsDelta > 0) || !(energyDelta >= 0)) return null;
  return Math.round(Math.sqrt(energyDelta / secsDelta) * 1000) / 1000;
}

function delta(cur: number, prev: number | undefined): number {
  return prev === undefined ? -1 : Math.max(0, cur - prev);
}

/** The message (without its type) for one window. A count of -1 means this
 * track was not there at the last sample, so the window is incomplete. */
export function healthReport(input: HealthInputs): Record<string, unknown> {
  const { prev, cur } = input;
  const before = new Map<string, InboundCounter>((prev?.inbound ?? []).map((c) => [c.mid, c]));
  const secs = prev ? Math.max(1, Math.round((cur.at - prev.at) / 1000)) : 0;
  const byTrack = new Map(cur.inbound.map((c) => [c.trackId, c] as const));
  const peers = input.peers.map((peer) => {
    const out: Record<string, unknown> = { id: peer.id };
    const audio = peer.audioTrackId ? byTrack.get(peer.audioTrackId) : undefined;
    if (audio) {
      const was = before.get(audio.mid);
      out.a = {
        p: delta(audio.packets, was?.packets),
        lvl: was ? meanLevel(audio.energy - was.energy, audio.energySecs - was.energySecs) : null,
        play: input.playing.has(audio.trackId),
        m: audio.muted,
      };
    } else {
      out.a = null;
    }
    // Packets arriving for this person on a section the tile is NOT playing:
    // the shape of a pull that landed on the wrong track.
    const other = cur.inbound.filter((c) => c.participantId === peer.id && c.kind === "audio" && c.trackId !== peer.audioTrackId);
    const otherPackets = other.reduce((n, c) => n + Math.max(0, delta(c.packets, before.get(c.mid)?.packets)), 0);
    if (otherPackets > 0) out.ao = otherPackets;
    for (const [key, trackId] of [["v", peer.videoTrackId], ["s", peer.screenTrackId]] as const) {
      const v = trackId ? byTrack.get(trackId) : undefined;
      if (!v) continue;
      const was = before.get(v.mid);
      out[key] = {
        f: delta(v.frames, was?.frames),
        w: v.width,
        h: v.height,
        fps: v.fps === null ? null : Math.round(v.fps),
        // How good the picture is, not only whether there is one (row 452):
        // kilobits a second, packets lost and freezes in the window.
        ...(was && secs > 0 ? { kb: Math.round((Math.max(0, v.bytes - was.bytes) * 8) / secs / 1000) } : {}),
        ...(was ? { lost: Math.max(0, v.lost - was.lost), fz: Math.max(0, v.freezes - was.freezes) } : {}),
      };
    }
    return out;
  });
  const o = cur.out;
  const p = prev?.out;
  return {
    secs,
    out: {
      a: p ? Math.max(0, o.audioPackets - p.audioPackets) : -1,
      lvl: p && o.micEnergy !== null && p.micEnergy !== null && o.micSecs !== null && p.micSecs !== null ? meanLevel(o.micEnergy - p.micEnergy, o.micSecs - p.micSecs) : null,
      v: p ? Math.max(0, o.videoFrames - p.videoFrames) : -1,
      mic: input.micOn,
      cam: input.cameraOn,
      ...(input.sharing ? { share: true } : {}),
      ...(input.hidden ? { hidden: true } : {}),
      ...(input.micDead ? { dead: true } : {}),
      ...(input.micTrack ? { trk: input.micTrack } : {}),
      ...(input.net ? { net: input.net } : {}),
      // What the camera is sending and what holds it back, so a bad picture
      // on the other side can be traced to this uplink or not.
      ...(p && secs > 0 && input.cameraOn ? { vk: Math.round((Math.max(0, o.videoBytes - p.videoBytes) * 8) / secs / 1000) } : {}),
      ...(o.sendHeight !== null ? { sh: o.sendHeight } : {}),
      ...(o.limit ? { lim: o.limit } : {}),
      ...(o.uplinkBps !== null ? { up: Math.round(o.uplinkBps / 1000) } : {}),
      ...(o.rtt !== null ? { rtt: Math.round(o.rtt * 1000) } : {}),
    },
    peers,
    stray: cur.stray.filter((s) => s.packets > 0).length,
  };
}

/** How hearing one person stands, checked every two seconds so a problem
 * shows on screen within seconds rather than at the next report (Adam,
 * 2026-10-16: "what if they recognize the problem and leave or something in
 * under 10s?"). */
export type HearState = "ok" | "reconnecting" | "stuck" | "tap";

export interface HearWatch {
  readonly state: HearState;
  /** Consecutive ticks with no packets while they send. */
  readonly empty: number;
  /** Consecutive ticks with packets arriving and nothing playing them. */
  readonly unplayed: number;
  /** Ticks spent reconnecting. */
  readonly waited: number;
}

export const HEAR_OK: HearWatch = { state: "ok", empty: 0, unplayed: 0, waited: 0 };

export interface HearTick {
  /** Packets of their audio in this tick; -1 when it cannot be told (a new
   * track, the first tick). */
  readonly packets: number;
  /** They are unmuted and publishing audio. */
  readonly sending: boolean;
  /** An unpaused <audio> element is playing their track. */
  readonly playing: boolean;
}

/** One two-second tick. `act` is what the page should do now: pull their
 * audio again, or play the element again. Two empty ticks (four seconds) is
 * a loss, since Opus sends fifty packets a second even through silence; three
 * more without packets after a re-pull is stuck, which asks for a tap. */
export function hearStep(w: HearWatch, t: HearTick): { next: HearWatch; act: "repull" | "play" | null } {
  if (!t.sending || t.packets < 0) {
    return { next: !t.sending ? HEAR_OK : w, act: null };
  }
  if (t.packets === 0) {
    const empty = w.empty + 1;
    if (w.state === "ok" || w.state === "tap") {
      return empty >= 2 ? { next: { state: "reconnecting", empty, unplayed: 0, waited: 0 }, act: "repull" } : { next: { ...w, empty }, act: null };
    }
    if (w.state === "reconnecting") {
      const waited = w.waited + 1;
      return { next: waited >= 3 ? { state: "stuck", empty, unplayed: 0, waited } : { ...w, empty, waited }, act: null };
    }
    return { next: { ...w, empty }, act: null };
  }
  // Packets are arriving.
  if (t.playing) return { next: HEAR_OK, act: null };
  const unplayed = w.unplayed + 1;
  if (unplayed === 1) return { next: { state: w.state === "tap" ? "tap" : "ok", empty: 0, unplayed, waited: 0 }, act: "play" };
  return { next: { state: "tap", empty: 0, unplayed, waited: 0 }, act: "play" };
}

/** Whether this side's own microphone has sent pure digital silence (no
 * energy at all) for `ticks` ticks while switched on: a muted headset or a
 * dead device. A real microphone always picks up some room noise. */
export function micDead(energyDeltas: readonly (number | null)[], ticks = 4): boolean {
  const tail = energyDeltas.slice(-ticks);
  return tail.length >= ticks && tail.every((e) => e === 0);
}
